import type { ReactNode } from 'react'
import { Explicacao } from '../../../components/Explicacao'
import { Cartao } from '../../../components/ListaDeDados'
import { dataSimples } from '../../../lib/datas'
import type { DetalheDaOs } from '../../../types/ordemDeServico'
import { TERMOS_DA_OS } from '../termosDaOs'
import { OS } from '../textosDaOs'

/**
 * As seções 1 e 2 dos ANEXOS IA e IB: o que foi contratado, o que esta ordem pede e quem assina.
 *
 * **As duas assinaturas trazem a matrícula.** No modelo do contrato elas são as únicas linhas que
 * identificam pessoas, e a matrícula é o que permite conferir depois quem autorizou a despesa — o
 * nome sozinho não distingue dois servidores homônimos num município com dezenas de milhares.
 *
 * **`dataSimples` em toda data de assinatura.** Elas chegam como `AAAA-MM-DD` e vão impressas em
 * processo administrativo: `new Date('2026-05-06')` é meia-noite em UTC e volta 05/05/2026 no fuso
 * de Niterói. Um dia a menos numa assinatura é contestação com razão.
 *
 * @param props a ordem carregada
 * @returns as seções de identificação
 */
export function IdentificacaoDaOs({ ordem }: { ordem: DetalheDaOs }) {
  return (
    <div className="space-y-4">
      <Cartao titulo="O que foi contratado" descricao="Seção 1 do modelo do contrato">
        <dl className="space-y-4">
          <Dado rotulo="Contrato" valor={ordem.contrato} />
          <Dado rotulo="Objeto" valor={ordem.objeto} />
          <Dado
            rotulo={TERMOS_DA_OS.regimeDaOs.rotulo}
            ajuda={
              <Explicacao titulo={TERMOS_DA_OS.regimeDaOs.rotulo} texto={TERMOS_DA_OS.regimeDaOs.ajuda} />
            }
            valor={ordem.tipo ? `${OS.regimes[ordem.regime]} — ${OS.tipos[ordem.tipo]}` : OS.regimes[ordem.regime]}
          />
          <Dado rotulo={OS.competencia} valor={ordem.competencia ?? '—'} />
          <Dado
            rotulo={OS.periodo}
            valor={`${dataSimples(ordem.periodoInicio)} a ${dataSimples(ordem.periodoFim)}`}
          />
          <Dado rotulo={OS.emitidaEm} valor={dataSimples(ordem.emitidaEm)} />
        </dl>
      </Cartao>

      <Cartao titulo="O que esta ordem pede" descricao="Seções 2 e 3 do modelo do contrato">
        <dl className="space-y-4">
          <Dado rotulo="Unidade requisitante" valor={ordem.unidade} />
          <Dado rotulo="Solicitante" valor={`${ordem.solicitanteNome} — ${ordem.solicitanteEmail}`} />
          <Dado rotulo="Objetivo" valor={ordem.objetivo} />
          {ordem.requisitosNaoFuncionais && (
            <Dado rotulo="Requisitos não funcionais" valor={ordem.requisitosNaoFuncionais} />
          )}
          {ordem.artefatosFornecidos && <Dado rotulo="Artefatos fornecidos" valor={ordem.artefatosFornecidos} />}
          {ordem.artefatosAGerar && <Dado rotulo="Artefatos a gerar" valor={ordem.artefatosAGerar} />}
        </dl>
      </Cartao>

      <Cartao titulo="Quem assinou" descricao="As duas assinaturas do rodapé do modelo">
        <dl className="grid gap-4 sm:grid-cols-2">
          <Dado
            rotulo="Fiscal requisitante"
            ajuda={
              <Explicacao
                titulo={TERMOS_DA_OS.fiscalRequisitante.rotulo}
                texto={TERMOS_DA_OS.fiscalRequisitante.ajuda}
              />
            }
            valor={
              <>
                {ordem.fiscalRequisitanteNome}
                <span className="block text-xs text-slate-500">
                  Matrícula {ordem.fiscalRequisitanteMatricula} · assinou em{' '}
                  {dataSimples(ordem.fiscalRequisitanteAssinouEm)}
                </span>
              </>
            }
          />
          <Dado
            rotulo="Gestor do contrato"
            valor={
              <>
                {ordem.gestorNome}
                <span className="block text-xs text-slate-500">
                  Matrícula {ordem.gestorMatricula} · assinou em {dataSimples(ordem.gestorAssinouEm)}
                </span>
              </>
            }
          />
        </dl>
      </Cartao>
    </div>
  )
}

/**
 * Um par rótulo/valor.
 *
 * `<dl>` e não duas colunas de `<div>`: sem a marcação, "Objeto" e o texto do objeto viram dois
 * blocos sem relação para quem ouve a tela.
 */
function Dado({ rotulo, valor, ajuda }: { rotulo: string; valor: ReactNode; ajuda?: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1 text-xs font-medium text-slate-500">
        {rotulo}
        {ajuda}
      </dt>
      <dd className="mt-1 whitespace-pre-line text-sm text-slate-900">{valor}</dd>
    </div>
  )
}
